import {useState, FC, useEffect, useMemo} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  useMediaQuery,
} from '@mui/material';
import {billingHistory} from './BillingHistory.style';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import {ReactComponent as PdfIcon} from '@/assets/icons/pdf-icon.svg';
import {H6, MMMM_D_YYYY, defaultPageSize} from '@/utils/constants';
import {IFilterGroup, PaymentStatus} from '@/utils/types';
import Pagination from '@/shared/components/Pagination';
import {TABLES_ENTITIES} from '@/utils/enums';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {billingHistorySelector} from '@/store/organization-billingHistory/selector';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {getCurrentEventSelector} from '@/store/organization-event/selector';
import {getBillingHistoryList} from '@/store/organization-billingHistory/actions';
import Loader from '@/shared/components/Loader';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import dayjs from 'dayjs';
import {currencyFormatter} from '@/utils/helpers/currency';

const StatusColor: Record<PaymentStatus, string> = {
  Completed: 'var(--success-600)',
  Pending: 'var(--warning-600)',
  Failed: 'var(--error-600)',
  Refunded: 'var(--neutral-600)',
};

const BillingHistory: FC = () => {
  const dispatch = useAppDispatch();

  const [page, setPage] = useState<number>(1);
  const [searchName, setSearchName] = useState<string>('');
  const [status, setStatus] = useState<PaymentStatus | 'All'>('All');
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(true);

  const billingData = useAppSelector(billingHistorySelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);

  const isLaptop = useMediaQuery('(min-width:1200px)');

  const filterData: IFilterGroup[] = useMemo(
    () => [
      {
        title: 'Status',
        options: (['Completed', 'Pending', 'Failed', 'Refunded', 'All'] as const).map((title) => ({
          title,
          isSelected: status === title,
        })),
      },
    ],
    [status]
  );

  const getBillingHistoryData = async () => {
    if (!currentEvent?.id) {
      return;
    }
    setIsDataLoaded(false);

    const data = {
      page: isLaptop ? page : 1,
      pageSize: isLaptop ? defaultPageSize : 10000,
      eventId: currentEvent?.id,
      isOrderDesc: true,
      ...(searchName && {searchName}),
      ...(status !== 'All' && {status: status as PaymentStatus}),
    };
    try {
      await dispatch(getBillingHistoryList(data)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoaded(true);
    }
  };

  useEffect(() => {
    getBillingHistoryData();
  }, [page, currentEvent?.id, searchName, status]);

  const handlePressEnter = () => {
    // Trigger Add API call on Enter key press
  };
  const handleSearchChange = (value: string) => {
    setSearchName(value);
    setPage(1);
  };

  const handleFilterChange = (groupTitle: string, optionTitle: string, id?: number) => {
    if (groupTitle !== 'Status') {
      return;
    }
    setStatus((optionTitle ?? 'All') as PaymentStatus | 'All');
    setPage(1);
  };
  const handlePaginationChange = (step: number) => {
    setPage(step);
  };

  const isMobile = useMediaQuery('(max-width:768px)');

  const isDesktop = useMediaQuery('(min-width:1200px)');

  return (
    <Box sx={{position: 'relative', flex: '1', display: 'flex', flexDirection: 'column', height: '100%'}}>
      {isDesktop && (
        <Typography sx={billingHistory.tableTitle} variant={H6}>
          Billing History
        </Typography>
      )}
      <SearchHeader
        page={page}
        items={billingData?.totalCount < defaultPageSize ? billingData?.totalCount : defaultPageSize}
        total={billingData?.totalCount}
        parentClassName={{
          '& .MuiTextField-root': {
            width: isMobile ? '100%' : '300px',
          },
        }}
        onPressEnter={handlePressEnter}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        filterOption={filterData}
        searchPlaceholder="Search Invoices"
        unitName={TABLES_ENTITIES.INVOICES}
      />

      {/* scrollable content area takes remaining space */}
      <Box sx={{flex: '1', overflowY: 'auto', minHeight: 0}}>
        {isDataLoaded ? (
          billingData?.totalCount > 0 ? (
            <TableContainer>
              <Table sx={{minWidth: isMobile ? 100 : 650, width: '100%'}} aria-label="enhanced table">
                <colgroup>
                  <col style={{width: isMobile ? '150px' : '50%'}} />
                  {!isMobile && <col style={{width: '50%'}} />}
                  <col style={{width: isMobile ? '100px' : 'min-content'}} />
                  <col style={{width: isMobile ? '80px' : 'min-content'}} />
                  {!isMobile && <col style={{width: 'min-content'}} />}
                </colgroup>
                <TableHead>
                  <TableRow sx={{height: '32px'}}>
                    <TableCell>Invoice</TableCell>
                    {!isMobile && <TableCell>Billed to</TableCell>}
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    {!isMobile && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingData &&
                    billingData?.data.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={billingHistory.tableCell}>
                          <Box sx={[billingHistory.fileWrap, billingHistory.tableCell]}>
                            <Box sx={billingHistory.iconWrap}>
                              <PdfIcon />
                            </Box>
                            <Box>
                              <Typography sx={billingHistory.invoiceName}>{row.invoiceFileName}</Typography>
                              <p style={billingHistory.neutral300}>{dayjs(row.invoiceDate).format(MMMM_D_YYYY)}</p>
                            </Box>
                          </Box>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Box sx={billingHistory.tableCell}>
                              <p style={billingHistory.weight700}>{row.billedToName}</p>
                              <p style={billingHistory.neutral300}>{row.billedToEmail}</p>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell>
                          <Box sx={billingHistory.tableCell}>
                            <p style={billingHistory.amount}>{currencyFormatter.format(row.amount)}</p>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={billingHistory.tableCell}>
                            <Box sx={billingHistory.statusWrap(StatusColor[row.status])}>
                              <p className="status">{row.status}</p>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyTableData text="No data found" />
          )
        ) : (
          <Loader parentClassName={billingHistory.loader} size={50} />
        )}
      </Box>

      {isDataLoaded && billingData?.totalCount > 0 && (
        <Box sx={billingHistory.pagination}>
          <Pagination
            page={page}
            pageSize={defaultPageSize || 0}
            total={billingData?.totalCount}
            onChange={(step) => handlePaginationChange(step)}
          />
        </Box>
      )}
    </Box>
  );
};

export default BillingHistory;
